import React, { useState, useEffect, useRef } from 'react';
import { billingService } from '../../services/billingService';
import { paymentService } from '../../services/paymentService';
import { authService } from '../../authService';
import { supabase } from '../../supabaseClient';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  InputAdornment,
  Pagination,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  Description as DescriptionIcon,
  AccountCircle as AccountCircleIcon,
  History as HistoryIcon,
  CalendarMonth as CalendarIcon,
  Speed as SpeedIcon,
  AttachMoney as AttachMoneyIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CreditCard as CreditCardIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

const BillModal = ({ 
  isOpen, 
  onClose, 
  mode, 
  bill,
  preSelectedCustomer, 
  onSuccess,
  customerTypes,
  surchargeSettings 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [navigatingToPayment, setNavigatingToPayment] = useState(false);
  const [usageAlert, setUsageAlert] = useState('');
  const [readerName, setReaderName] = useState('');
  
  // Customer search
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [lastBill, setLastBill] = useState(null);
  const [customerHistory, setCustomerHistory] = useState([]);
  
  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [billToDelete, setBillToDelete] = useState(null);
  
  // Billing history pagination
  const [currentHistoryPage, setCurrentHistoryPage] = useState(1);
  const historyPerPage = 5;
  
  // Form data
  const [formData, setFormData] = useState({
    customerid: '',
    billedmonth: '',
    previousreading: 0,
    currentreading: 0,
    consumption: 0,
    basicamount: 0
  });

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && bill) {
        loadEditMode();
      } else if (mode === 'add' && preSelectedCustomer) {
        loadPreSelectedCustomer();
      } else {
        resetForm();
      }
    }
  }, [isOpen, mode, bill, preSelectedCustomer]);

  const currentReadingRef = useRef(null);

  useEffect(() => {
    if (isOpen && mode === 'add' && selectedCustomer) {
      // Auto-focus Current Reading field when customer is selected in Add mode
      const timer = setTimeout(() => {
        if (currentReadingRef.current) {
          currentReadingRef.current.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, mode, selectedCustomer]);

  const loadEditMode = async () => {
    const customer = {
      customerid: bill.customerid,
      name: bill.customers?.name || '',
      type: bill.customers?.type || '',
      barangay: bill.customers?.barangay || '',
      discount: bill.customers?.discount || 0
    };
    setSelectedCustomer(customer);
    setFormData({
      customerid: bill.customerid,
      billedmonth: bill.billedmonth.substring(0, 7), // Extract YYYY-MM format
      previousreading: bill.previousreading,
      currentreading: bill.currentreading,
      consumption: bill.consumption,
      basicamount: bill.basicamount
    });
    
    // Load customer history
    const historyResult = await billingService.getCustomerBills(bill.customerid);
    if (historyResult.success) {
      setCustomerHistory(historyResult.data);
      setCurrentHistoryPage(1); // Reset to first page
    }
    setUsageAlert('');
    await loadReaderForBarangay(customer.barangay);
  };

  const loadPreSelectedCustomer = async () => {
    // Pre-selected customer from billing page
    await handleSelectCustomer(preSelectedCustomer);
  };

  const resetForm = () => {
    setSelectedCustomer(null);
    setCustomerResults([]);
    setCustomerSearch('');
    setLastBill(null);
    setCustomerHistory([]);
    setError('');
    setUsageAlert('');
    setReaderName('');
    setFormData({
      customerid: '',
      billedmonth: getCurrentMonth(),
      previousreading: 0,
      currentreading: 0,
      consumption: 0,
      basicamount: 0
    });
  };

  const getCurrentMonth = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  };

  const handleCustomerSearch = async () => {
    if (!customerSearch.trim()) {
      setError('Please enter customer name or ID');
      return;
    }
    
    setLoading(true);
    setError('');
    const result = await paymentService.searchCustomers(customerSearch);
    
    if (result.success) {
      setCustomerResults(result.data);
      if (result.data.length === 0) {
        setError('No customers found');
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const loadReaderForBarangay = async (barangay) => {
    if (!barangay) {
      setReaderName('');
      return;
    }
    try {
      const { data, error } = await supabase
        .from('barangays')
        .select('reader')
        .eq('barangay', barangay)
        .maybeSingle();

      if (error) {
        console.error('Error loading reader for barangay:', error);
        setReaderName('');
        return;
      }

      setReaderName(data?.reader || '');
    } catch (e) {
      console.error('Error loading reader for barangay:', e);
      setReaderName('');
    }
  };

  const handleSelectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setCustomerResults([]);
    setCustomerSearch('');
    
    // Load last bill for previous reading
    const lastBillResult = await billingService.getLastBill(customer.customerid);
    let prevReading = 0;

    if (lastBillResult.success && lastBillResult.data) {
      setLastBill(lastBillResult.data);
      prevReading = parseFloat(lastBillResult.data.currentreading || 0);
    } else {
      setLastBill(null);
      prevReading = 0;
    }
    
    // Calculate initial basic amount for 0 consumption (since current = previous initially)
    let initialBasicAmount = 0;
    const customerType = customerTypes.find(ct => ct.type === customer.type);
    
    if (customerType) {
        // Consumption is 0 initially because current = previous
        initialBasicAmount = billingService.calculateBasicAmount(0, customerType);
    }
    
    // Load customer billing history
    const historyResult = await billingService.getCustomerBills(customer.customerid);
    if (historyResult.success) {
      setCustomerHistory(historyResult.data);
      setCurrentHistoryPage(1); // Reset to first page
    }
    setUsageAlert('');
    await loadReaderForBarangay(customer.barangay);
    
    // Set next billed month (automatically set to month after last bill)
    const nextMonth = getNextBilledMonth(lastBillResult.data?.billedmonth);
    
    setFormData({
      customerid: customer.customerid,
      billedmonth: nextMonth,
      previousreading: prevReading,
      currentreading: prevReading,
      consumption: 0,
      basicamount: initialBasicAmount
    });
  };

  const getNextBilledMonth = (lastBilledMonth) => {
    if (!lastBilledMonth) {
      return getCurrentMonth();
    }
    // lastBilledMonth comes from DB as YYYY-MM-DD, extract YYYY-MM
    const yearMonth = lastBilledMonth.substring(0, 7);
    const date = new Date(yearMonth + '-01');
    date.setMonth(date.getMonth() + 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  };

  const handleFormChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    
    // Auto-calculate consumption
    if (field === 'previousreading' || field === 'currentreading') {
      const prev = parseFloat(field === 'previousreading' ? value : newFormData.previousreading) || 0;
      const curr = parseFloat(field === 'currentreading' ? value : newFormData.currentreading) || 0;
      newFormData.consumption = Math.max(0, curr - prev);
    }
    
    // Auto-calculate basic amount
    if (field === 'consumption' || field === 'previousreading' || field === 'currentreading') {
      const customerType = customerTypes.find(ct => ct.type === selectedCustomer?.type);
      if (customerType && newFormData.consumption >= 0) {
        newFormData.basicamount = billingService.calculateBasicAmount(newFormData.consumption, customerType);
      }
    }

    if (field === 'currentreading' && mode === 'add') {
      if (customerHistory && customerHistory.length > 0) {
        const consumptions = customerHistory
          .map(h => parseFloat(h.consumption || 0))
          .filter(v => !isNaN(v) && v > 0);

        if (consumptions.length > 0) {
          const total = consumptions.reduce((sum, v) => sum + v, 0);
          const avg = total / consumptions.length;
          const diff = newFormData.consumption - avg;

          if (diff > 10) {
            const baseMessage =
              `Current consumption ${newFormData.consumption.toFixed(2)} m³ exceeds the average usage of ` +
              `${avg.toFixed(2)} m³ by ${diff.toFixed(2)} m³.`;

            if (readerName && readerName.trim()) {
              setUsageAlert(
                `${baseMessage} Please coordinate with ${readerName.trim()} to verify the customer's meter reading.`
              );
            } else {
              setUsageAlert(
                `${baseMessage} Please coordinate with the assigned reader to verify the customer's meter reading.`
              );
            }
          } else {
            setUsageAlert('');
          }
        } else {
          setUsageAlert('');
        }
      } else {
        setUsageAlert('');
      }
    }
    
    setFormData(newFormData);
  };

  const generateUniqueBillId = async () => {
    // Generate random 8-digit number (10000000 to 99999999)
    const generateRandom8Digit = () => {
      return Math.floor(10000000 + Math.random() * 90000000);
    };
    
    let billId = generateRandom8Digit();
    let attempts = 0;
    const maxAttempts = 10;
    
    // Check if billId exists and generate new one if it does
    while (attempts < maxAttempts) {
      const { data, error } = await billingService.checkBillIdExists(billId);
      if (error) {
        throw new Error('Error checking bill ID: ' + error);
      }
      if (!data.exists) {
        return billId;
      }
      billId = generateRandom8Digit();
      attempts++;
    }
    
    throw new Error('Could not generate unique bill ID after ' + maxAttempts + ' attempts');
  };

  const validateForm = async () => {
    if (!selectedCustomer) {
      setError('Please select a customer');
      return false;
    }
    
    if (!formData.billedmonth) {
      setError('Please select billing month');
      return false;
    }
    
    if (parseFloat(formData.currentreading) < parseFloat(formData.previousreading)) {
      setError('Current reading cannot be less than previous reading');
      return false;
    }
    
    // Check for duplicate bill (convert month format for checking)
    const billedMonthForCheck = formData.billedmonth + '-01';
    const duplicateCheck = await billingService.checkDuplicateBill(
      formData.customerid,
      billedMonthForCheck,
      mode === 'edit' ? bill?.billid : null
    );
    
    if (duplicateCheck.exists) {
      setError('A bill for this customer and month already exists');
      return false;
    }
    
    return true;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!await validateForm()) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Get current logged-in user
      const currentUser = authService.getUserSession();
      
      const billData = {
        customerid: formData.customerid,
        billedmonth: formData.billedmonth + '-01', // Convert YYYY-MM to YYYY-MM-DD
        previousreading: parseFloat(formData.previousreading) || 0,
        currentreading: parseFloat(formData.currentreading) || 0,
        consumption: parseFloat(formData.consumption) || 0,
        basicamount: parseFloat(formData.basicamount) || 0,
        surchargeamount: 0,
        discountamount: 0,
        totalbillamount: 0,
        paymentstatus: 'Unpaid',
        encodedby: currentUser ? `${currentUser.firstname} ${currentUser.lastname}` : 'Unknown',
        reader: readerName && readerName.trim() ? readerName.trim() : null
      };
      
      let result;
      if (mode === 'add') {
        // Generate unique 8-digit bill ID
        const uniqueBillId = await generateUniqueBillId();
        billData.billid = uniqueBillId;
        result = await billingService.addBill(billData);
      } else {
        result = await billingService.updateBill(bill.billid, billData);
      }
      
      if (result.success) {
        onSuccess(`Bill ${mode === 'add' ? 'added' : 'updated'} successfully!`);
        onClose();
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError(error.message);
    }
    
    setLoading(false);
  };

  const formatCurrency = (amount) => {
    return `₱${parseFloat(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatMonth = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'long'
    });
  };

  const handleProcessPayment = () => {
    if (!selectedCustomer) {
      setError('Please select a customer first');
      return;
    }
    
    setNavigatingToPayment(true);
    // Store customer data in sessionStorage for PaymentsPage to pick up
    sessionStorage.setItem('preSelectedCustomerForPayment', JSON.stringify(selectedCustomer));
    
    // Dispatch event to navigate to payments page
    window.dispatchEvent(new CustomEvent('navigateToPayments', { 
      detail: { customer: selectedCustomer } 
    }));
    
    onClose();
  };

  return (
    <>
      <Dialog 
        open={isOpen} 
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '60vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon />
            <Typography variant="h6">{mode === 'add' ? 'Add New Bill' : 'Edit Bill'}</Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Customer Search Section (only in Add mode and no customer selected) */}
          {!selectedCustomer && mode === 'add' && (
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Select Customer</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="Search by customer name or ID..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleCustomerSearch()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleCustomerSearch}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  >
                    Search
                  </Button>
                </Box>

                {customerResults.length > 0 && (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Barangay</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {customerResults.map((customer) => (
                          <TableRow key={customer.customerid} hover>
                            <TableCell>{customer.customerid}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{customer.name}</TableCell>
                            <TableCell><Chip label={customer.type} size="small" color="info" variant="outlined" /></TableCell>
                            <TableCell>{customer.barangay}</TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleSelectCustomer(customer)}
                              >
                                Select
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          )}

          {/* Selected Customer & Bill Form */}
          {selectedCustomer && (
            <Stack spacing={3}>
              {/* Customer Info Card */}
              <Card variant="outlined" sx={{ bgcolor: 'primary.50', borderColor: 'primary.main' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AccountCircleIcon color="primary" sx={{ mr: 1, fontSize: 32 }} />
                    <Typography variant="h6">Customer Information</Typography>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">ID</Typography>
                      <Typography variant="body1" fontWeight="medium">{selectedCustomer.customerid}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">Name</Typography>
                      <Typography variant="body1" fontWeight="medium">{selectedCustomer.name}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">Type</Typography>
                      <Box><Chip label={selectedCustomer.type} size="small" color="info" /></Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">Barangay</Typography>
                      <Typography variant="body1" fontWeight="medium">{selectedCustomer.barangay}</Typography>
                    </Grid>
                    
                    {lastBill && (
                      <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
                          <HistoryIcon color="action" fontSize="small" />
                          <Typography variant="body2">
                            Last Reading: <strong>{lastBill.currentreading} cu.m</strong> ({formatMonth(lastBill.billedmonth)})
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* Bill Details Form */}
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <DescriptionIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Bill Details</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 220 }}>
                      <TextField
                        fullWidth
                        type="month"
                        label="Billing Month"
                        value={formData.billedmonth}
                        onChange={(e) => handleFormChange('billedmonth', e.target.value)}
                        onKeyDown={handleKeyDown}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                        helperText="Select month"
                      />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <TextField
                        fullWidth
                        label="Previous Reading"
                        type="number"
                        value={formData.previousreading}
                        onChange={(e) => handleFormChange('previousreading', e.target.value)}
                        onKeyDown={handleKeyDown}
                        inputProps={{ step: "0.01" }}
                        required
                        helperText={lastBill ? `Last: ${lastBill.currentreading}` : "Enter reading"}
                        error={parseFloat(formData.previousreading) < 0}
                        FormHelperTextProps={{ role: 'alert' }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <TextField
                        fullWidth
                        label="Current Reading"
                        type="number"
                        value={formData.currentreading}
                        onChange={(e) => handleFormChange('currentreading', e.target.value)}
                        onKeyDown={handleKeyDown}
                        inputProps={{ step: "0.01" }}
                        inputRef={currentReadingRef}
                        required
                        helperText="Enter reading"
                        error={parseFloat(formData.currentreading) < 0 || parseFloat(formData.currentreading) < parseFloat(formData.previousreading)}
                        FormHelperTextProps={{ role: 'alert' }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <TextField
                        fullWidth
                        label="Consumption"
                        value={formData.consumption}
                        InputProps={{
                          readOnly: true,
                          style: { fontWeight: 'bold', color: '#1976d2' }
                        }}
                        helperText="Auto-calculated"
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <TextField
                        fullWidth
                        label="Basic Amount"
                        value={formData.basicamount}
                        InputProps={{
                          readOnly: true,
                          startAdornment: <InputAdornment position="start">₱</InputAdornment>,
                          style: { fontWeight: 'bold', color: '#1976d2' }
                        }}
                        helperText="Auto-calculated"
                      />
                    </Box>
                  </Box>

                  {usageAlert && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="warning" onClose={() => setUsageAlert('')}>
                        {usageAlert}
                      </Alert>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Billing History Table */}
              {customerHistory.length > 0 && (
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <HistoryIcon color="info" sx={{ mr: 1 }} />
                      <Typography variant="h6">Billing History</Typography>
                    </Box>
                    
                    <TableContainer>
                      <Table size="small" sx={{ minWidth: 650 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Month</TableCell>
                            <TableCell>Reading</TableCell>
                            <TableCell>Consumption</TableCell>
                            <TableCell>Basic Amount</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {customerHistory
                            .slice((currentHistoryPage - 1) * historyPerPage, currentHistoryPage * historyPerPage)
                            .map((h) => (
                            <TableRow key={h.billid} hover>
                              <TableCell>{formatMonth(h.billedmonth)}</TableCell>
                              <TableCell>{h.previousreading} → {h.currentreading}</TableCell>
                              <TableCell>{h.consumption} cu.m</TableCell>
                              <TableCell>{formatCurrency(h.basicamount)}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={h.paymentstatus} 
                                  size="small" 
                                  color={
                                    h.paymentstatus === 'Paid' ? 'success' :
                                    h.paymentstatus === 'Partial' ? 'warning' : 'error'
                                  } 
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Stack direction="row" spacing={1} justifyContent="center">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => {
                                      const billWithCustomer = { ...h, customers: selectedCustomer };
                                      onClose();
                                      setTimeout(() => {
                                        window.dispatchEvent(new CustomEvent('editBill', { detail: billWithCustomer }));
                                      }, 100);
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      setBillToDelete(h);
                                      setShowDeleteConfirm(true);
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {customerHistory.length > historyPerPage && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Pagination 
                          count={Math.ceil(customerHistory.length / historyPerPage)} 
                          page={currentHistoryPage} 
                          onChange={(e, p) => setCurrentHistoryPage(p)}
                          size="small"
                          color="primary"
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: 'grey.50', justifyContent: 'space-between' }}>
          <Box>
            <Button onClick={onClose} color="inherit" sx={{ mr: 1 }}>Cancel</Button>
            {selectedCustomer && (
              <Button 
                variant="outlined" 
                color="info" 
                startIcon={<CreditCardIcon />}
                onClick={handleProcessPayment}
                disabled={navigatingToPayment}
              >
                Process Payment
              </Button>
            )}
          </Box>
          <Button 
            variant="contained" 
            color="primary"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={!selectedCustomer || loading}
          >
            {mode === 'add' ? 'Add Bill' : 'Update Bill'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Delete Bill?
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to delete this bill? This action cannot be undone.
          </Typography>
          {billToDelete && (
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">Billing Month:</Typography>
              <Typography variant="subtitle2" gutterBottom>{formatMonth(billToDelete.billedmonth)}</Typography>
              
              <Typography variant="body2" color="text.secondary">Amount:</Typography>
              <Typography variant="subtitle2" color="primary">{formatCurrency(billToDelete.basicamount)}</Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => {
              onClose();
              setShowDeleteConfirm(false);
              setTimeout(() => {
                const billWithCustomer = { ...billToDelete, customers: selectedCustomer };
                window.dispatchEvent(new CustomEvent('deleteBill', { detail: billWithCustomer }));
              }, 100);
              setBillToDelete(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BillModal;
